/** API e stato server del modulo connettori broker (SPEC-011 §8). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssetDomain } from '@/lib/asset-domain';
import { api } from '@/lib/api-client';

export type BrokerEnvironment = 'demo' | 'live';

/** `broker` (Trading 212) o `crypto_exchange` (Bitpanda): la UI sceglie il modal da qui, senza conoscere i provider. */
export type ConnectionKind = 'broker' | 'crypto_exchange';

/** Cosa sa fare un exchange: la UI mostra o nasconde comandi da qui, non dal nome del provider. */
export type ExchangeCapabilities = {
  /** `orders` = libro con ordini pendenti; `quotes` = quotazione immediata, eseguita subito. */
  tradingModel: 'orders' | 'quotes';
  orderTypes: Array<'market' | 'limit' | 'stop' | 'stop_limit'>;
  timeInForce: Array<'gtc' | 'gtd' | 'ioc' | 'fok'>;
  openOrders: boolean;
  operations: boolean;
  candles: boolean;
  feeTier: boolean;
  cryptoQuotedPairs: boolean;
  nonCryptoAssets: boolean;
};

export type BrokerConnection = {
  id: string;
  provider: string;
  kind: ConnectionKind;
  /** `null` per un broker. */
  capabilities: ExchangeCapabilities | null;
  /** Gli ambiti serviti dal provider (SPEC-026 §2.2). */
  domains: AssetDomain[];
  environment: BrokerEnvironment;
  label: string;
  status: 'active' | 'error' | 'revoked';
  tradingEnabled: boolean;
  /** Entrambe le condizioni di SPEC-011 §7.1 soddisfatte: il calcolo lo fa il backend, mai la UI. */
  canPlaceOrders: boolean;
  accountRef: string | null;
  accountCurrency: string | null;
  /** Prefisso della fingerprint: distingue due chiavi senza rivelarne nessuna. */
  credentialsHint: string;
  portfolio: { id: string; name: string };
  lastVerifiedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  historyComplete: boolean;
  createdAt: string;
};

export type SyncRun = {
  id: string;
  /** `partial` non è un errore: lo storico del broker si scarica in più giri. */
  status: 'running' | 'completed' | 'partial' | 'failed';
  trigger: string;
  startedAt: string;
  finishedAt: string | null;
  positionsSynced: number;
  transactionsCreated: number;
  transactionsSkipped: number;
  instrumentsCreated: number;
  pagesFetched: number;
  hasMore: boolean;
  error: string | null;
};

export type StreamState = { cursor: string | null; syncedThrough: string | null; complete: boolean };

export type BrokerConnectionDetail = BrokerConnection & {
  /** Tre flussi per un broker, uno solo (`trades`) per un exchange. */
  syncState:
    | { orders: StreamState; dividends: StreamState; transactions: StreamState }
    | { trades: StreamState };
  recentRuns: SyncRun[];
};

export type CreateConnectionInput = {
  label: string;
  environment: BrokerEnvironment;
  apiKey: string;
  apiSecret: string;
};

export const connectorKeys = {
  all: ['connectors'] as const,
  detail: (id: string) => ['connectors', id] as const,
};

export function useConnections() {
  return useQuery({
    queryKey: connectorKeys.all,
    queryFn: ({ signal }) => api.get<{ items: BrokerConnection[] }>('/connectors', signal),
  });
}

export function useConnection(id: string | null) {
  return useQuery({
    queryKey: connectorKeys.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<BrokerConnectionDetail>(`/connectors/${id}`, signal),
    enabled: id !== null,
  });
}

export function useCreateTrading212Connection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConnectionInput) => api.post<BrokerConnection>('/connectors/trading212', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.all });
      // Il collegamento crea anche un portafoglio: la sua lista è ora obsoleta.
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    },
  });
}

export type CreateBitpandaConnectionInput = {
  label: string;
  apiKey: string;
  /** Valuta di conto del portafoglio crypto (EUR per difetto). */
  quoteCurrency?: string;
};

export type ExchangeProvider = 'bitpanda' | 'bitpanda_public';

const EXCHANGE_CREATE_PATH: Record<ExchangeProvider, string> = {
  bitpanda: '/connectors/bitpanda',
  bitpanda_public: '/connectors/bitpanda-public',
};

/**
 * Bitpanda Fusion e Bitpanda Public API (SPEC-024): una sola chiave, nessun
 * ambiente — e' sempre denaro reale. Le due chiavi sono DISTINTE: il provider
 * decide la rotta.
 */
export function useCreateBitpandaConnection(provider: ExchangeProvider = 'bitpanda') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBitpandaConnectionInput) => api.post<BrokerConnection>(EXCHANGE_CREATE_PATH[provider], input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    },
  });
}

export function useUpdateConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; label?: string; tradingEnabled?: boolean }) =>
      api.patch<BrokerConnection>(`/connectors/${id}`, patch),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.all });
      void queryClient.invalidateQueries({ queryKey: connectorKeys.detail(variables.id) });
    },
  });
}

export function useVerifyConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<BrokerConnection>(`/connectors/${id}/verify`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.all });
    },
  });
}

export function useSyncConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<SyncRun>(`/connectors/${id}/sync`),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.all });
      void queryClient.invalidateQueries({ queryKey: connectorKeys.detail(id) });
      // La sincronizzazione scrive snapshot e transazioni: portafogli e
      // dashboard mostrano dati ora superati.
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Esito dello svuotamento: quanto è stato cancellato, e quanto di quello
 * tornerà da solo. `transactions - fromBroker` è la parte che **non** torna.
 */
export type ResetResyncResult = {
  deleted: { transactions: number; fromBroker: number; snapshots: number };
  run: SyncRun;
};

/**
 * Svuota il portafoglio collegato e lo riscarica da capo.
 *
 * `confirm: true` viaggia nel corpo perché il backend lo pretende: la conferma
 * dell'interfaccia vive nel browser, e la rotta è raggiungibile senza passare
 * di lì.
 */
export function useResetResync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<ResetResyncResult>(`/connectors/${id}/reset-resync`, { confirm: true }),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.all });
      void queryClient.invalidateQueries({ queryKey: connectorKeys.detail(id) });
      // Movimenti e snapshot sono stati riscritti da zero: tutto ciò che li
      // legge sta mostrando dati che non esistono più.
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/connectors/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.all });
    },
  });
}

// -----------------------------------------------------------------------------
// Mappatura strumento → ticker Trading 212 (SPEC-014, rischio R1)
// -----------------------------------------------------------------------------

export type TickerMatch = {
  ticker: string;
  name: string;
  isin: string | null;
  currency: string | null;
  type: string | null;
  /** `true` quando l'ISIN coincide: è la corrispondenza di cui fidarsi. */
  isinMatch: boolean;
};

/**
 * Ricerca **a comando**, non a ogni battitura: il catalogo del broker si scarica
 * con una richiesta ogni 50 secondi (arriva completo e non paginato), quindi il
 * primo giro può richiedere qualche secondo. `useMutation` invece di `useQuery`
 * proprio per questo — la chiamata parte quando l'utente la chiede.
 */
export function useTickerSearch() {
  return useMutation({
    mutationFn: ({ connectionId, query, instrumentId }: { connectionId: string; query: string; instrumentId?: string }) => {
      const params = new URLSearchParams({ q: query });
      if (instrumentId) params.set('instrumentId', instrumentId);
      return api.get<{ items: TickerMatch[] }>(`/connectors/${connectionId}/ticker-search?${params.toString()}`);
    },
  });
}

export type TickerProposal = {
  instrumentId: string;
  chosenTicker: string | null;
  confidence: number;
  reason: string;
  runnerUpTicker: string | null;
  /** Calcolato dal backend, non dichiarato dal modello. */
  nameMatch: boolean;
  /** Confidenza sopra soglia **e** nome coerente: le due condizioni insieme. */
  autoAssignable: boolean;
  match: TickerMatch | null;
  connectionUsed: string | null;
  assigned: boolean;
  skippedReason: string | null;
};

/**
 * Proposta automatica del ticker (F-120 fase D).
 *
 * ⚠️ **Propone, non assegna.** L'endpoint non scrive mai da questa via: la
 * scrittura resta il gesto dell'utente che conferma. L'assegnazione automatica
 * esiste per i percorsi in cui nessuno puo' confermare — un job notturno — e
 * vive nel backend, non qui.
 */
export function useProposeTicker() {
  return useMutation({
    mutationFn: ({ instrumentId, connectionId }: { instrumentId: string; connectionId?: string }) => {
      const params = connectionId ? `?connectionId=${encodeURIComponent(connectionId)}` : '';
      return api.post<TickerProposal>(`/connectors/instruments/${instrumentId}/ticker-propose${params}`, {});
    },
  });
}

export function useAssignTicker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ instrumentId, ticker }: { instrumentId: string; ticker: string | null }) =>
      api.patch<{ instrumentId: string; trading212Ticker: string | null }>(
        `/connectors/instruments/${instrumentId}/ticker`,
        { trading212Ticker: ticker },
      ),
    onSuccess: () => {
      // La mappatura compare nella wishlist (che la mostra) e condiziona ciò che
      // l'auto-trading può eseguire: entrambe le viste sono ora superate.
      void queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      void queryClient.invalidateQueries({ queryKey: ['auto-trading'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Ordine manuale: anteprima (TODO.md)
// -----------------------------------------------------------------------------

export type OrderPreview = {
  ticker: string;
  instrumentName: string;
  side: 'buy' | 'sell';
  /** Stringhe decimali, mai number (ADR-0006). */
  referencePrice: string | null;
  referencePriceAt: string | null;
  /** Quantità che verrà davvero inviata, già troncata verso il basso. */
  quantity: string | null;
  /** Con «vendi tutto»: le azioni possedute all'ultima sincronizzazione. */
  heldQuantity: string | null;
  /** ⚠️ Quando quella posizione è stata fotografata: NON è il conto di adesso. */
  heldAsOf: string | null;
  /** ⚠️ Ciò che resterà in portafoglio perché il broker non accetta più decimali. */
  residualQuantity: string | null;
  estimatedAmount: string | null;
  /** `true` = l'invio può partire. Non è una garanzia: i limiti si consumano all'invio. */
  canSubmit: boolean;
  blockers: string[];
  warnings: string[];
  limitsSummary: string;
  environment: string;
};

export type PreviewOrderInput = {
  connectionId: string;
  instrumentId: string;
  side: 'buy' | 'sell';
  amount?: string;
  quantity?: string;
  /** «Vendi tutto»: la quantità la mette il server, prendendo quella posseduta. */
  sellAll?: boolean;
  /** Una frazione della posizione, 1-100 (F-139). Il 100 è «vendi tutto». */
  sellPercent?: number;
};

export function usePreviewOrder() {
  return useMutation({
    mutationFn: ({ connectionId, ...body }: PreviewOrderInput) =>
      api.post<OrderPreview>(`/connectors/${connectionId}/orders/preview`, body),
  });
}

export type PlaceOrderInput = {
  connectionId: string;
  ticker: string;
  side: 'buy' | 'sell';
  quantity: number;
};

export function usePlaceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, ...body }: PlaceOrderInput) =>
      api.post<{ id: string; state: string; error: string | null }>(`/connectors/${connectionId}/orders`, {
        ...body,
        orderType: 'market',
      }),
    onSuccess: () => {
      // Un ordine cambia posizioni e movimenti: portafogli e dashboard mostrano
      // dati ora superati.
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: connectorKeys.all });
    },
  });
}

// -----------------------------------------------------------------------------
// Exchange crypto: catalogo, saldi, ordini e scambi (SPEC-024 §8)
// -----------------------------------------------------------------------------

export type ExchangeAsset = {
  symbol: string;
  name: string;
  type: 'crypto' | 'fiat';
  /** Un conto Bitpanda tiene anche azioni, ETF e metalli. */
  kind: 'crypto' | 'fiat' | 'stock' | 'etf' | 'metal' | 'commodity' | 'other';
  isin: string | null;
};

/** I vincoli arrivano come stringhe decimali o null: l'exchange puo' non dichiararli. */
export type ExchangePair = {
  pair: string;
  baseAsset: string;
  quoteAsset: string;
  baseType: 'crypto' | 'fiat';
  quoteType: 'crypto' | 'fiat';
  tickSize: string | null;
  sizeIncrement: string | null;
  amountIncrement: string | null;
  minOrderAmount: string | null;
  maxOrderAmount: string | null;
  maxOrderSize: string | null;
};

export type CryptoCatalog = {
  fetchedAt: string;
  capabilities: ExchangeCapabilities;
  assets: ExchangeAsset[];
  pairs: ExchangePair[];
  total: number;
};

export type ExchangeBalance = { asset: string; available: string; locked: string };

export type ExchangeOrder = {
  id: string;
  pair: string;
  side: 'buy' | 'sell';
  type: 'market' | 'limit' | 'stop' | 'stop_limit';
  status: 'open' | 'partially_filled' | 'filled' | 'cancelled' | 'rejected' | 'expired' | 'unknown';
  statusRaw: string;
  quantity: string | null;
  amount: string | null;
  limitPrice: string | null;
  triggerPrice: string | null;
  filledQuantity: string | null;
  filledAmount: string | null;
  createdAt: string | null;
};

export const cryptoKeys = {
  catalog: (id: string) => ['connectors', id, 'crypto', 'catalog'] as const,
  balances: (id: string) => ['connectors', id, 'crypto', 'balances'] as const,
  openOrders: (id: string) => ['connectors', id, 'crypto', 'orders', 'open'] as const,
};

/** Il catalogo cambia raramente: il backend lo tiene in cache un'ora, qui basta non richiederlo a ogni render. */
export function useCryptoCatalog(connectionId: string | null) {
  return useQuery({
    queryKey: cryptoKeys.catalog(connectionId ?? ''),
    queryFn: ({ signal }) => api.get<CryptoCatalog>(`/connectors/${connectionId}/crypto/catalog`, signal),
    enabled: connectionId !== null,
    staleTime: 10 * 60 * 1000,
  });
}

/** Saldi LIVE dell'exchange: sono la fonte del «vendi tutto», non lo snapshot. */
export function useCryptoBalances(connectionId: string | null) {
  return useQuery({
    queryKey: cryptoKeys.balances(connectionId ?? ''),
    queryFn: ({ signal }) => api.get<{ items: ExchangeBalance[] }>(`/connectors/${connectionId}/crypto/balances`, signal),
    enabled: connectionId !== null,
  });
}

export function useExchangeOpenOrders(connectionId: string | null) {
  return useQuery({
    queryKey: cryptoKeys.openOrders(connectionId ?? ''),
    queryFn: ({ signal }) => api.get<{ items: ExchangeOrder[] }>(`/connectors/${connectionId}/crypto/orders?status=open`, signal),
    enabled: connectionId !== null,
  });
}

export function useCancelExchangeOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, orderId }: { connectionId: string; orderId: string }) =>
      api.delete<void>(`/connectors/${connectionId}/crypto/orders/${encodeURIComponent(orderId)}`),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: cryptoKeys.openOrders(variables.connectionId) });
      void queryClient.invalidateQueries({ queryKey: cryptoKeys.balances(variables.connectionId) });
    },
  });
}

export type CryptoOrderInput = {
  pair: string;
  side: 'buy' | 'sell';
  orderType: 'market' | 'limit' | 'stop' | 'stop_limit';
  /** Stringhe decimali (ADR-0006). Uno solo fra quantity, amount, sellPercent. */
  quantity?: string;
  amount?: string;
  sellPercent?: number;
  limitPrice?: string;
  triggerPrice?: string;
  timeInForce?: 'gtc' | 'gtd' | 'ioc' | 'fok';
  endTime?: string;
};

export type CryptoOrderPreview = {
  pair: string;
  baseAsset: string;
  quoteAsset: string;
  tradingModel: 'orders' | 'quotes';
  /** La quote e' una crypto: e' uno scambio, non passa dalla cassa. */
  isSwap: boolean;
  side: 'buy' | 'sell';
  orderType: string;
  sizedBy: 'quantity' | 'amount';
  referencePrice: string | null;
  referencePriceAt: string | null;
  quantity: string | null;
  amount: string | null;
  estimatedAccountValue: string | null;
  accountCurrency: string;
  availableBalance: string | null;
  heldQuantity: string | null;
  residualQuantity: string | null;
  estimatedFee: string | null;
  feePct: string | null;
  constraints: {
    sizeIncrement: string | null;
    amountIncrement: string | null;
    minOrderAmount: string | null;
    maxOrderAmount: string | null;
    maxOrderSize: string | null;
  };
  canSubmit: boolean;
  blockers: string[];
  warnings: string[];
  limitsSummary: string;
  environment: string;
};

export type CryptoOrder = {
  id: string;
  externalOrderId: string | null;
  pair: string;
  side: string;
  orderType: string;
  quantity: string | null;
  quoteAmount: string | null;
  state: string;
  statusRaw: string | null;
  swapGroupId: string | null;
  error: string | null;
};

export function usePreviewCryptoOrder() {
  return useMutation({
    mutationFn: ({ connectionId, ...body }: CryptoOrderInput & { connectionId: string }) =>
      api.post<CryptoOrderPreview>(`/connectors/${connectionId}/crypto/orders/preview`, body),
  });
}

export function usePlaceCryptoOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, ...body }: CryptoOrderInput & { connectionId: string }) =>
      api.post<CryptoOrder>(`/connectors/${connectionId}/crypto/orders`, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: cryptoKeys.balances(variables.connectionId) });
      void queryClient.invalidateQueries({ queryKey: cryptoKeys.openOrders(variables.connectionId) });
    },
  });
}

export type SwapInput = { fromAsset: string; toAsset: string; quantity?: string; percent?: number };

export type SwapLegPreview = {
  pair: string;
  side: 'buy' | 'sell';
  sizedBy: 'quantity' | 'amount';
  quantity: string | null;
  amount: string | null;
  referencePrice: string | null;
  estimatedFee: string | null;
  blockers: string[];
};

export type SwapPreview = {
  fromAsset: string;
  toAsset: string;
  /** `direct` = una coppia sola; `via_quote` = due gambe attraverso la valuta di conto; `none` = impossibile. */
  route: 'direct' | 'via_quote' | 'none';
  via: string | null;
  legs: SwapLegPreview[];
  fromQuantity: string | null;
  availableFrom: string | null;
  residualQuantity: string | null;
  estimatedToQuantity: string | null;
  estimatedAccountValue: string | null;
  accountCurrency: string;
  canSubmit: boolean;
  blockers: string[];
  warnings: string[];
  limitsSummary: string;
  environment: string;
};

export type SwapResult = {
  swapGroupId: string;
  /** `partial` = la prima gamba e' partita, la seconda no: il messaggio dice perche'. */
  status: 'completed' | 'partial' | 'failed';
  legs: CryptoOrder[];
  message: string;
};

export function usePreviewSwap() {
  return useMutation({
    mutationFn: ({ connectionId, ...body }: SwapInput & { connectionId: string }) =>
      api.post<SwapPreview>(`/connectors/${connectionId}/crypto/swap/preview`, body),
  });
}

export function useExecuteSwap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, ...body }: SwapInput & { connectionId: string }) =>
      api.post<SwapResult>(`/connectors/${connectionId}/crypto/swap`, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: cryptoKeys.balances(variables.connectionId) });
      void queryClient.invalidateQueries({ queryKey: cryptoKeys.openOrders(variables.connectionId) });
    },
  });
}
